import { useState, useEffect } from 'react';
import { X, Film, Copy, Check, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

export interface SceneData {
  sceneNumber: number;
  title: string;
  content: string;
}

export interface ScenesData {
  totalScenes: number;
  scenes: SceneData[];
}

export interface VideoExampleWithScenes {
  id: string;
  title: string;
  description: string | null;
  video_url: string | null;
  thumbnail_url: string | null;
  prompt_text: string | null;
  duration: string | null;
  display_order: number;
  scenes_data: ScenesData | null;
}

interface VideoExampleModalProps {
  example: VideoExampleWithScenes;
  isOpen: boolean;
  onClose: () => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
  hasNavigation?: boolean;
}

export function VideoExampleModal({
  example,
  isOpen,
  onClose,
  onNavigate,
  hasNavigation = false,
}: VideoExampleModalProps) {
  const [copiedScene, setCopiedScene] = useState<number | null>(null);
  const [copiedFull, setCopiedFull] = useState(false);
  const [expandedScenes, setExpandedScenes] = useState<Set<number>>(new Set());

  // Reset expanded scenes when video changes
  useEffect(() => {
    setExpandedScenes(new Set());
  }, [example.id]);

  const toggleScene = (sceneNumber: number) => {
    setExpandedScenes(prev => {
      const next = new Set(prev);
      if (next.has(sceneNumber)) {
        next.delete(sceneNumber);
      } else {
        next.add(sceneNumber);
      }
      return next;
    });
  };

  const scenesData = example.scenes_data;

  const copyToClipboard = async (text: string, sceneNumber?: number) => {
    try {
      await navigator.clipboard.writeText(text);
      if (sceneNumber !== undefined) {
        setCopiedScene(sceneNumber);
        setTimeout(() => setCopiedScene(null), 2000);
      } else {
        setCopiedFull(true);
        setTimeout(() => setCopiedFull(false), 2000);
      }
      toast.success('Copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const getFullPrompt = () => {
    if (!scenesData) return example.prompt_text || '';
    return scenesData.scenes
      .map((scene) => `Scene ${scene.sceneNumber}: ${scene.title}\n${scene.content}`)
      .join('\n\n');
  };

  // Helper to detect if URL is a direct video file
  const isDirectVideo = (url: string | null) => {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      return /\.(mp4|webm|mov)$/i.test(urlObj.pathname);
    } catch {
      return false;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-7xl w-[95vw] max-h-[90vh] p-0 bg-card border-border overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 p-2 rounded-full bg-background/80 hover:bg-background transition-colors"
        >
          <X className="w-5 h-5 text-foreground" />
        </button>

        {/* Navigation Arrows - Outside the modal content area */}
        {hasNavigation && onNavigate && (
          <>
            <button
              onClick={() => onNavigate('prev')}
              className="fixed left-2 sm:left-4 top-1/2 -translate-y-1/2 z-[60] p-3 rounded-full bg-background/90 hover:bg-background border border-border shadow-lg transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-foreground" />
            </button>
            <button
              onClick={() => onNavigate('next')}
              className="fixed right-2 sm:right-4 top-1/2 -translate-y-1/2 z-[60] p-3 rounded-full bg-background/90 hover:bg-background border border-border shadow-lg transition-colors"
            >
              <ChevronRight className="w-6 h-6 text-foreground" />
            </button>
          </>
        )}

        {/* Accessibility - Hidden title and description */}
        <VisuallyHidden>
          <DialogTitle>{example.title}</DialogTitle>
          <DialogDescription>
            Video example with {scenesData?.totalScenes || 0} scenes. {example.description}
          </DialogDescription>
        </VisuallyHidden>

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-foreground">{example.title}</h2>
            {scenesData && (
              <Badge variant="secondary" className="text-xs">
                {scenesData.totalScenes} scenes - Ready for Veo 3
              </Badge>
            )}
          </div>
        </div>

        {/* Two-column layout */}
        <div className="flex flex-col lg:flex-row gap-6 p-6 h-[calc(90vh-80px)] overflow-hidden">
          {/* Left: Video Player */}
          <div className="lg:w-1/2 flex flex-col min-h-0">
            <div className="flex items-center gap-2 mb-3">
              <Film className="w-5 h-5 text-primary" />
              <h3 className="font-semibold text-foreground">Video Example</h3>
            </div>

            {/* Video */}
            <div className="aspect-video bg-black rounded-lg overflow-hidden flex-shrink-0">
              {example.video_url ? (
                isDirectVideo(example.video_url) ? (
                  <video
                    src={example.video_url}
                    controls
                    autoPlay
                    className="w-full h-full object-contain"
                    title={example.title}
                  />
                ) : (
                  <iframe
                    src={example.video_url}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title={example.title}
                  />
                )
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-muted">
                  <p className="text-muted-foreground">Video coming soon</p>
                </div>
              )}
            </div>

            {/* Video title under player */}
            <p className="text-center mt-3 text-sm text-muted-foreground">{example.title}</p>

            {/* Copy Full Prompt button */}
            <Button
              className="w-full mt-4"
              onClick={() => copyToClipboard(getFullPrompt())}
            >
              {copiedFull ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Full Prompt
                </>
              )}
            </Button>
          </div>

          {/* Right: Scenes/Prompt Panel with ScrollArea */}
          <div className="lg:w-1/2 flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-foreground">
                {scenesData ? 'Scene Prompts' : 'Video Details'}
              </h3>
              {scenesData && (
                <Badge variant="outline">{scenesData.totalScenes} Scenes Total</Badge>
              )}
            </div>

            <ScrollArea className="flex-1 pr-4">
              {scenesData && scenesData.scenes.length > 0 ? (
                <div className="space-y-2">
                  {scenesData.scenes.map((scene) => {
                    const isExpanded = expandedScenes.has(scene.sceneNumber);
                    return (
                      <Collapsible
                        key={scene.sceneNumber}
                        open={isExpanded}
                        onOpenChange={() => toggleScene(scene.sceneNumber)}
                      >
                        <div className="border border-border rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors overflow-hidden">
                          {/* Collapsed header - always visible */}
                          <div className="flex items-center justify-between p-3">
                            <CollapsibleTrigger asChild>
                              <button className="flex items-center gap-2 flex-1 text-left">
                                <ChevronDown 
                                  className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`} 
                                />
                                <Badge variant="secondary" className="text-xs">
                                  Scene {scene.sceneNumber}
                                </Badge>
                                <span className="font-medium text-foreground text-sm truncate">
                                  {scene.title}
                                </span>
                              </button>
                            </CollapsibleTrigger>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(
                                  `Scene ${scene.sceneNumber}: ${scene.title}\n${scene.content}`,
                                  scene.sceneNumber
                                );
                              }}
                              className="h-7 px-2 ml-2 shrink-0"
                            >
                              {copiedScene === scene.sceneNumber ? (
                                <Check className="w-3 h-3 text-green-500" />
                              ) : (
                                <Copy className="w-3 h-3" />
                              )}
                            </Button>
                          </div>

                          {/* Expanded content */}
                          <CollapsibleContent>
                            <div className="px-3 pb-3 pt-0">
                              <p className="whitespace-pre-wrap text-sm text-muted-foreground leading-relaxed pl-6">
                                {scene.content}
                              </p>
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    );
                  })}
                </div>
              ) : (
                // Fallback for videos without scenes_data - show description and/or prompt
                <div className="space-y-4">
                  {example.description && (
                    <div className="p-4 border border-border rounded-lg bg-muted/30">
                      <h4 className="text-sm font-semibold text-foreground mb-2">Description</h4>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {example.description}
                      </p>
                    </div>
                  )}
                  {example.prompt_text && (
                    <div className="p-4 border border-border rounded-lg bg-muted/30">
                      <h4 className="text-sm font-semibold text-foreground mb-2">Prompt Used</h4>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap font-mono">
                        {example.prompt_text}
                      </p>
                    </div>
                  )}
                  {!example.description && !example.prompt_text && (
                    <div className="p-4 border border-border rounded-lg bg-muted/30 text-center">
                      <p className="text-sm text-muted-foreground">
                        Prompt details coming soon
                      </p>
                    </div>
                  )}
                  {example.duration && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Film className="w-4 h-4" />
                      <span>Duration: {example.duration}</span>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
